import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { DataImportSummarySchema } from "@/domains/openapi/types";
import { Uuid } from "@/domains/global/identifiers";
import { Button } from "@radix-ui/themes";
import { DateTime } from "luxon";
import { DataImportsTable } from "@/pages/data-imports/shared/data-imports-table";

export const SpaceAccountDataImportsPage: FC = () => {
  const navigate = useNavigate();
  const { spaceAccountId } = useParams<{ spaceAccountId: Uuid }>();

  if (!spaceAccountId) {
    return <div>Invalid ID</div>;
  }

  const pageSize = 1000;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["space-account-data-imports", spaceAccountId],
    queryFn: () =>
      api.post("/internal/v1/data-imports/search", {
        body: {
          filter_by_space_account_id: spaceAccountId,
          limit: pageSize,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const handleViewDataImport = (id: string) => {
    navigate(atlasRoutes.dataImportsView({ params: { dataImportId: id } }).path);
  };

  const handleViewSpaceAccount = () => {
    navigate(atlasRoutes.spaceAccountsView({ params: { spaceAccountId } }).path);
  };

  const tableData = useMemo<DataImportSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <h1>Data Imports</h1>

      <Button variant="outline" onClick={() => handleViewSpaceAccount()}>
        View Space Account
      </Button>
      <br />
      <br />

      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching data imports: {error.message}</div>}

      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <DataImportsTable
          data={tableData}
          onViewDataImport={handleViewDataImport}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && <div>No data imports found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;
